import React, { useEffect } from 'react';
import { useRealChat } from '../contexts/RealChatContext';
import './ChatList.css';

const ChatList: React.FC = () => {
  const { 
    conversations, 
    showChatList, 
    setShowChatList, 
    setActiveConversation, 
    setShowChat,
    markAsRead,
    loading,
    currentUser
  } = useRealChat();

  useEffect(() => {
    // Cargar conversaciones cuando se abre la lista
    if (showChatList && currentUser) {
      // Las conversaciones se cargan automáticamente en el contexto
    }
  }, [showChatList, currentUser]);

  const formatTime = (date: Date) => {
    // Verificar si la fecha es válida
    if (!date || isNaN(date.getTime())) {
      return 'Reciente';
    }

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString();
  };

  const formatNameWithInitials = (name: string, apellido?: string) => {
    // Si hay apellido separado, usarlo directamente
    if (apellido) {
      const firstName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
      const lastName = apellido.charAt(0).toUpperCase() + apellido.slice(1).toLowerCase();
      const formatted = `${firstName} ${lastName}`;
      return formatted;
    }
    
    // Si no hay apellido separado, intentar dividir el nombre
    const parts = name.split(' ');
    
    if (parts.length >= 2) {
      const firstName = parts[0];
      const lastName = parts[parts.length - 1];
      const formatted = `${firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()} ${lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase()}`;
      return formatted;
    }
    const singleName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    return singleName;
  };

  const getOtherParticipant = (conversation: any) => {
    if (!currentUser) return null;
    
    // Buscar el participante que NO sea el usuario actual
    if (conversation.participant1Id === currentUser.id) {
      return {
        id: conversation.participant2Id,
        name: conversation.participant2Name,
        apellido: conversation.participant2Apellido || '', // Agregar apellido si existe
        type: conversation.participant2Type
      };
    } else {
      return {
        id: conversation.participant1Id,
        name: conversation.participant1Name,
        apellido: conversation.participant1Apellido || '', // Agregar apellido si existe
        type: conversation.participant1Type
      };
    }
  };

  const handleConversationClick = (conversation: any) => {
    // Marcar como leída inmediatamente para quitar el estado azul
    if (conversation?.id) {
      markAsRead(conversation.id);
    }
    setActiveConversation(conversation);
    setShowChat(true);
    setShowChatList(false);
  };

  if (!showChatList) return null;

  return (
    <div className="chat-list-overlay" onClick={() => setShowChatList(false)}>
      <div className="chat-list-container" onClick={(e) => e.stopPropagation()}>
        <div className="chat-list-header">
          <h3>Mensajes</h3>
          <button 
            className="close-btn"
            onClick={() => setShowChatList(false)}
          >
            ✕
          </button>
        </div>

        <div className="chat-list-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Cargando conversaciones...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💬</div>
              <h4>No tienes conversaciones</h4>
              <p>Cuando alguien te contacte, aparecerá aquí</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const otherParticipant = getOtherParticipant(conversation);
              if (!otherParticipant) return null;

              const unread = (conversation.unreadCount || 0) > 0;
              return (
                <div
                  key={conversation.id}
                  className={`conversation-item ${unread ? 'unread' : ''}`}
                  onClick={() => handleConversationClick(conversation)}
                >
                  <div className="conversation-avatar">
                    <div className="avatar-circle">
                      {otherParticipant.name.charAt(0).toUpperCase()}
                    </div>
                    {unread && (
                      <div className="unread-badge">{conversation.unreadCount}</div>
                    )}
                  </div>

                  <div className="conversation-content">
                    <div className="conversation-header">
                      <h4 className="conversation-name">{formatNameWithInitials(otherParticipant.name, otherParticipant.apellido)}</h4>
                      <span className="conversation-time">
                        {conversation.lastMessage ? formatTime(conversation.lastMessage.timestamp) : ''}
                      </span>
                    </div>

                    <div className="conversation-preview">
                      {conversation.jobTitle && (
                        <span className="job-title">{conversation.jobTitle}</span>
                      )}
                      {conversation.lastMessage ? (
                        <p className="last-message">
                          {conversation.lastMessage.type === 'location' && '📍 '}
                          {conversation.lastMessage.type === 'image' && '🖼️ '}
                          {conversation.lastMessage.type === 'file' && '📎 '}
                          {conversation.lastMessage.content}
                        </p>
                      ) : (
                        <p className="last-message">Empieza la conversación…</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatList;